import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Activity, Clock, TrendingUp, Zap } from 'lucide-react';
import { SHEET_ANIMATION, BACKDROP_ANIMATION } from './sheetAnimationConfig';
/**
 * MarketSheet - Bottom sheet for selecting market type/game status
 * Handles different filtering options based on sport:
 * - Football: all, live, prematch (markets)
 * - NFL: all, live, upcoming (game status)
 * - MLB, NBA, etc: To be added later
 */
const MarketSheet = ({ isOpen, onClose, currentMarket, onMarketChange, sport = 'football' }) => {
  // Different options based on sport
  // TODO: Add market/status options for MLB, NBA, NCAAF, NCAAB as needed
  const getMarketOptions = () => {
    switch (sport) {
      case 'nfl':
        // NFL uses game status filtering (live/upcoming)
        return [
          { id: 'all', label: 'All Games', icon: TrendingUp, description: 'Show all games' },
          { id: 'live', label: 'Live', icon: Zap, description: 'Games in progress', color: 'red' },
          { id: 'upcoming', label: 'Upcoming', icon: Clock, description: 'Games not started' }
        ];
      
      case 'football':
      default:
        // Football uses market type filtering (live/prematch)
        return [
          { id: 'all', label: 'All Markets', icon: TrendingUp, description: 'Show all available games' },
          { id: 'live', label: 'Live', icon: Activity, description: 'In-play games only' },
          { id: 'prematch', label: 'Pre-match', icon: Clock, description: 'Upcoming games only' }
        ];
      
      // Future sports can be added here:
      // case 'mlb':
      // case 'nba':
      // case 'ncaaf':
      // case 'ncaab':
    }
  };

  const marketOptions = getMarketOptions();

  const handleMarketSelect = (marketId) => {
    onMarketChange(marketId);
    onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            {...BACKDROP_ANIMATION}
            className="fixed inset-0 bg-black/50 z-40"
            onClick={onClose}
          />
          
          {/* Sheet */}
          <motion.div
            {...SHEET_ANIMATION}
            className="fixed bottom-0 left-0 right-0 bg-gray-900 rounded-t-2xl z-50 max-h-[40vh] overflow-hidden"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-800">
              <h3 className="text-lg font-semibold text-white">
                {sport === 'nfl' ? 'Game Status' : 'Market Type'}
              </h3>
              <button
                onClick={onClose}
                className="p-1 rounded-lg hover:bg-gray-800 transition-colors"
              >
                <X className="w-5 h-5 text-gray-400" />
              </button>
            </div>
            
            {/* Market Options */}
            <div className="p-2">
              {marketOptions.map((option) => {
                const Icon = option.icon;
                const isSelected = currentMarket === option.id;
                const isLive = option.id === 'live' && option.color === 'red'; // NFL live games
                
                return (
                  <button
                    key={option.id}
                    onClick={() => handleMarketSelect(option.id)}
                    className={`
                      w-full flex items-center gap-3 p-3 rounded-lg transition-all
                      ${isSelected 
                        ? isLive 
                          ? 'bg-red-500/20 border border-red-500/50'
                          : 'bg-emerald-500/20 border border-emerald-500/50' 
                        : 'hover:bg-gray-800/50'
                      }
                    `}
                  >
                    <div className={`
                      p-2 rounded-lg
                      ${isSelected 
                        ? isLive ? 'bg-red-500/20' : 'bg-emerald-500/20' 
                        : 'bg-gray-800'}
                    `}>
                      <Icon className={`
                        w-4 h-4
                        ${isSelected 
                          ? isLive ? 'text-red-400' : 'text-emerald-400' 
                          : 'text-gray-400'}
                      `} />
                    </div>
                    
                    <div className="flex-1 text-left">
                      <div className={`
                        font-medium text-sm flex items-center gap-2
                        ${isSelected 
                          ? isLive ? 'text-red-400' : 'text-emerald-400' 
                          : 'text-white'}
                      `}>
                        {option.label}
                        {isLive && isSelected && (
                          <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                        )}
                      </div>
                      <div className="text-xs text-gray-500 mt-0.5">
                        {option.description}
                      </div>
                    </div>
                    
                    {isSelected && (
                      <div className="w-2 h-2 rounded-full bg-emerald-400" />
                    )}
                  </button>
                );
              })}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default MarketSheet;